// @greenlight-spec: 44-marketplace-listings.md#entitlement--metering-architecture
import { z } from 'zod';
// Chosen by GREENLIGHT_BILLING_PROVIDER, never derived from CLOUD_PROVIDERS:
// most paying installs are direct contracts, not marketplace transactions, and
// a marketplace-born install is stamped with its cloud value at install time.
export const BILLING_PROVIDERS = ['direct', 'none', 'azure', 'gcp', 'aws'];
/**
 * Every verdict a `BillingProvider` can resolve, across all of them.
 * `suspended`/`unsubscribed` are marketplace vocabulary; `grace` and `lapsed`
 * only a dated term can have, and they arrive from the fleet report response
 * (types/fleet-entitlement.ts). The two halves are disjoint in practice — no
 * provider resolves both — which is why the enforcement state machine can
 * treat `lapsed` as already-degraded while a marketplace lapse still runs the
 * grace clock.
 *
 * Appended rather than slotted in alphabetically so the `entitlement_state`
 * Postgres enum can be extended with `ADD VALUE` instead of rebuilt.
 */
export const ENTITLEMENT_STATES = [
    'entitled',
    'suspended',
    'unsubscribed',
    'unknown',
    'grace',
    'lapsed',
];
/** Closed set of metered units; rates live in the marketplace offer, never here. */
export const USAGE_DIMENSIONS = ['deployments', 'mau_per_app', 'app_hours'];
export const EntitlementStatusSchema = z.object({
    state: z.enum(ENTITLEMENT_STATES),
    /** ISO-8601; null = no fixed term. */
    expires_at: z.string().datetime({ offset: true }).nullable(),
    /** Marketplace plan / contract identifier. */
    plan: z.string().nullable(),
    /**
     * Why, in the source's own closed vocabulary (a fleet entitlement reason for
     * a direct contract); marketplace providers carry none.
     */
    reason: z.string().max(64).nullable().optional(),
});
/** Metered usage for one dimension over a closed window. */
export const UsageReportSchema = z.object({
    dimension: z.enum(USAGE_DIMENSIONS),
    /** Whole units for the window. */
    quantity: z.number().int().nonnegative(),
    /** ISO-8601, inclusive. */
    window_start: z.string().datetime({ offset: true }),
    /** ISO-8601, exclusive. */
    window_end: z.string().datetime({ offset: true }),
});
export const UsageReceiptSchema = z.object({
    accepted: z.boolean(),
    /** Window already reported — treated as success by callers. */
    duplicate: z.boolean(),
    /** Marketplace-side usage event id, for audit. */
    provider_ref: z.string().nullable(),
});
export const BillingEnforcementStateSchema = z.enum(['ok', 'warning', 'degraded']);
/**
 * Install-level entitlement enforcement state (docs/44 §Enforcement). `warning`
 * surfaces the "building will be disabled on `degrade_at`" banner; `degraded`
 * closes the new-work surfaces. Only a positively-resolved lapse
 * (`unsubscribed`/`suspended`) advances the state — `unknown` means the source
 * could not be read and holds the install at `ok`.
 *
 * `billing_source` distinguishes a marketplace subscription from a direct
 * contract so the banner and the `403`'s `next_steps` can name a remedy the
 * admin actually has: telling a direct-contract admin to check a marketplace
 * subscription they never had is worse than saying nothing.
 */
export const BillingEntitlementEnforcementSchema = z.object({
    state: BillingEnforcementStateSchema,
    state_since: z.string().datetime({ offset: true }),
    entitlement_state: z.enum(ENTITLEMENT_STATES),
    billing_source: z.enum(BILLING_PROVIDERS),
    plan: z.string().nullable(),
    /** The resolved entitlement's reason, when its source names one. */
    entitlement_reason: z.string().nullable(),
    /**
     * The instant new work pauses if nothing changes: a grace deadline, a
     * marketplace lapse's local clock, or the day a payment method is due. Null
     * when there is nothing to count to.
     */
    degrade_at: z.string().datetime({ offset: true }).nullable(),
});
//# sourceMappingURL=billing.js.map